<template>
  <v-card flat class="pa-5 bg-pwt_snow">
    <!-- fleet-wide search & filters -->
    <v-card-text class="px-0 px-md-4">
      <v-row no-gutters class="ga-2">
        <v-col>
          <v-text-field
            v-model="searchQuery"
            bg-color="background"
            clearable
            placeholder="Search fleet vehicles..."
          >
            <template #append-inner>
              <v-menu
                v-model="filterMenuOpen"
                :close-on-content-click="false"
                location="bottom end"
              >
                <template #activator="{ props: menuActivatorProps }">
                  <v-icon v-bind="menuActivatorProps" icon="mdi-filter-variant" />
                </template>
                <v-card min-width="240" class="pa-4">
                  <div class="text-caption text-grey mb-1">Fuel type</div>
                  <v-checkbox
                    v-for="option in fuelTypeOptions"
                    :key="option.value"
                    v-model="selectedFuelTypes"
                    :value="option.value"
                    :label="option.label"
                    density="compact"
                    hide-details
                  />
                  <v-divider class="my-3" />
                  <div class="text-caption text-grey mb-1">Compliance status</div>
                  <v-checkbox
                    v-for="option in issueFilterOptions"
                    :key="option.value"
                    v-model="selectedIssueFilters"
                    :value="option.value"
                    :label="option.label"
                    density="compact"
                    hide-details
                  />
                  <v-divider class="my-3" />
                  <div class="text-caption text-grey mb-1">RUC</div>
                  <v-checkbox
                    v-for="option in rucFilterOptions"
                    :key="option.value"
                    v-model="selectedRucFilters"
                    :value="option.value"
                    :label="option.label"
                    density="compact"
                    hide-details
                  />
                </v-card>
              </v-menu>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="5" md="4">
          <v-select
            v-model="sortOption"
            :items="SORT_OPTIONS"
            item-title="label"
            item-value="value"
            bg-color="background"
            hide-details
          />
        </v-col>
      </v-row>

      <div
        v-if="
          selectedFuelTypes.length ||
          selectedIssueFilters.length ||
          selectedRucFilters.length ||
          notifiedVehicleExternalIds?.length
        "
      >
        <v-chip
          v-if="notifiedVehicleExternalIds?.length"
          closable
          size="small"
          class="mr-2 mt-2"
          @click:close="clearNotifiedVehicleIds"
        >
          From notification ({{ notifiedVehicleExternalIds.length }})
        </v-chip>
        <v-chip
          v-for="fuelType in selectedFuelTypes"
          :key="'fuel-chip-' + fuelType"
          closable
          size="small"
          class="mr-2 mt-2"
          @click:close="removeFuelType(fuelType)"
        >
          {{ fuelTypeLabel(fuelType) }}
        </v-chip>
        <v-chip
          v-for="issueFilter in selectedIssueFilters"
          :key="'issue-chip-' + issueFilter"
          closable
          size="small"
          class="mr-2 mt-2"
          @click:close="removeIssueFilter(issueFilter)"
        >
          {{ issueFilterLabel(issueFilter) }}
        </v-chip>
        <v-chip
          v-for="rucFilter in selectedRucFilters"
          :key="'ruc-chip-' + rucFilter"
          closable
          size="small"
          class="mr-2 mt-2"
          @click:close="removeRucFilter(rucFilter)"
        >
          {{ rucFilterLabel(rucFilter) }}
        </v-chip>
      </div>
    </v-card-text>

    <!-- saved non-connected vehicles two column list -->
    <v-card-text class="text-h6 px-0 px-md-4">
      <span> Non-telematics connected vehicles </span>
      <v-row v-if="filteredCustomVehicles.length" no-gutters>
        <v-card
          flat
          v-for="(vehicle, index) in filteredCustomVehicles"
          :key="'saved-vehicle-' + index"
          :style="smAndDown ? 'width: 100%' : 'width: 50%'"
          class="py-1 pa-md-1 overflow-visible"
          color="transparent"
        >
          <DashboardVehicleListCard
            :vehicle="vehicle"
            @click="navigateToVehicleDetails(vehicle)"
            height="100%"
          />
        </v-card>
      </v-row>
      <v-row
        v-else-if="hasActiveSearchOrFilters && vehicleStore.getCustomVehicles.length"
        class="text-body-1 text-grey my-4"
        no-gutters
      >
        No vehicles match your search.
      </v-row>
      <v-row v-else class="text-body-1 text-grey my-4" no-gutters>
        You have no non-telematics vehicles
      </v-row>
    </v-card-text>
    <!-- fleet vehicles two column list -->
    <v-card-text class="text-h6 px-0 px-md-4">
      <span> Telematics connected vehicles </span>
      <v-row v-if="filteredConnectedVehicles.length" no-gutters>
        <v-card
          flat
          v-for="(vehicle, index) in filteredConnectedVehicles"
          :key="'fleet-vehicle-' + index"
          :style="smAndDown ? 'width: 100%' : 'width: 50%'"
          class="py-1 pa-md-1 overflow-visible"
          color="transparent"
        >
          <DashboardVehicleListCard
            :vehicle="vehicle"
            @click="navigateToVehicleDetails(vehicle)"
            height="100%"
          />
        </v-card>
      </v-row>
      <v-row
        v-else-if="hasActiveSearchOrFilters && vehicleStore.getConnectedVehicles.length"
        class="text-body-1 text-grey my-4"
        no-gutters
      >
        No vehicles match your search.
      </v-row>
      <v-row v-else class="text-body-1 text-grey my-4" no-gutters>
        You have no connected vehicles
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { useInspectionHistoryStore } from "@/domains/inspectionHistory";
import { useRucStore } from "@/domains/ruc";
import { useVehicleStore, type Vehicle } from "@/domains/vehicles";
import {
  VEHICLE_ISSUE_FILTER_OPTIONS,
  vehicleMatchesIssueFilter,
  type VehicleIssueFilter,
} from "@/domains/vehicles/vehicleComplianceStatus";
import {
  VEHICLE_RUC_FILTER_OPTIONS,
  vehicleMatchesRucFilter,
  type VehicleRucFilter,
} from "@/domains/vehicles/vehicleRucStatus";
import { useDashboardNavStore } from "@/stores/dashboardNavStore";
import { computed, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import DashboardVehicleListCard from "./DashboardVehicleListCard.vue";

type SortOption = "none" | "wofCof" | "registration";

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "none", label: "Sort: default order" },
  { value: "wofCof", label: "Sort: WOF/COF due soonest" },
  { value: "registration", label: "Sort: registration due soonest" },
];

const { smAndDown } = useDisplay();

const vehicleStore = useVehicleStore();
const inspectionHistoryStore = useInspectionHistoryStore();
const rucStore = useRucStore();
const dashboardNavStore = useDashboardNavStore();

// Vuetify's clearable text field sets the model to null on clear, not "".
const searchQuery = ref<string | null>("");
const filterMenuOpen = ref(false);
const selectedFuelTypes = ref<string[]>([]);
const selectedIssueFilters = ref<VehicleIssueFilter[]>([]);
const selectedRucFilters = ref<VehicleRucFilter[]>([]);
const sortOption = ref<SortOption>("none");
const notifiedVehicleExternalIds = ref<string[] | null>(null);

function isRucFilter(
  filter: VehicleIssueFilter | VehicleRucFilter,
): filter is VehicleRucFilter {
  return VEHICLE_RUC_FILTER_OPTIONS.some((option) => option.value === filter);
}

/**
 * Seeds the filter with whatever VehicleIssuesSummaryCard.vue's stat click
 * (via dashboardNavStore.showVehicleList) asked for, then clears it -
 * pendingVehicleListIssueFilter is a one-shot handoff, not a permanent
 * binding, so the user can freely change/clear the filter afterwards.
 * `immediate` covers the case where it's already set when this component
 * (re)activates. It can carry either a compliance filter or a RUC filter,
 * so it's routed to the matching selected-filters array - and the *other*
 * array is cleared, so a stat click always shows exactly the vehicles it
 * counted rather than ANDing with whatever filter was left over from a
 * previous visit.
 */
watch(
  () => dashboardNavStore.pendingVehicleListIssueFilter,
  (filter) => {
    if (!filter) return;

    if (isRucFilter(filter)) {
      selectedRucFilters.value = [filter];
      selectedIssueFilters.value = [];
    } else {
      selectedIssueFilters.value = [filter];
      selectedRucFilters.value = [];
    }

    dashboardNavStore.pendingVehicleListIssueFilter = null;
  },
  { immediate: true },
);

/**
 * Same one-shot handoff as pendingVehicleListIssueFilter above, but for
 * narrowing display to a specific set of vehicles - e.g. a notification
 * about several vehicles at once (see resolveNotificationTarget's
 * "vehicle-group" kind).
 */
watch(
  () => dashboardNavStore.pendingVehicleListVehicleIds,
  (vehicleIds) => {
    if (!vehicleIds) return;

    notifiedVehicleExternalIds.value = vehicleIds;
    dashboardNavStore.pendingVehicleListVehicleIds = null;
  },
  { immediate: true },
);

function clearNotifiedVehicleIds() {
  notifiedVehicleExternalIds.value = null;
}

// Derived from whatever fuel types actually appear in the fleet, rather
// than hardcoded, so this stays in sync with Vehicle's fuelTypeDisplayText
// without duplicating that mapping here.
const fuelTypeOptions = computed(() => {
  const seen = new Map<string, string>();
  for (const vehicle of [
    ...vehicleStore.getCustomVehicles,
    ...vehicleStore.getConnectedVehicles,
  ]) {
    if (vehicle.fuelType) seen.set(vehicle.fuelType, vehicle.fuelTypeDisplayText);
  }
  return Array.from(seen, ([value, label]) => ({ value, label }));
});

const issueFilterOptions = VEHICLE_ISSUE_FILTER_OPTIONS;
const rucFilterOptions = VEHICLE_RUC_FILTER_OPTIONS;

function fuelTypeLabel(value: string): string {
  return (
    fuelTypeOptions.value.find((option) => option.value === value)?.label ??
    value
  );
}

function issueFilterLabel(value: VehicleIssueFilter): string {
  return (
    VEHICLE_ISSUE_FILTER_OPTIONS.find((option) => option.value === value)
      ?.label ?? value
  );
}

function removeFuelType(value: string) {
  selectedFuelTypes.value = selectedFuelTypes.value.filter((v) => v !== value);
}

function removeIssueFilter(value: VehicleIssueFilter) {
  selectedIssueFilters.value = selectedIssueFilters.value.filter(
    (v) => v !== value,
  );
}

function rucFilterLabel(value: VehicleRucFilter): string {
  return (
    VEHICLE_RUC_FILTER_OPTIONS.find((option) => option.value === value)
      ?.label ?? value
  );
}

function removeRucFilter(value: VehicleRucFilter) {
  selectedRucFilters.value = selectedRucFilters.value.filter(
    (v) => v !== value,
  );
}

/** Whole days from now until the given ISO date string (negative if past). */
function daysUntil(dateString: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.floor((new Date(dateString).getTime() - Date.now()) / msPerDay);
}

/**
 * Days until this vehicle's WOF/COF is next due, or +Infinity if it has no
 * compliance record - sorting ascending on this then naturally puts the
 * most overdue vehicle first and vehicles with no record last, with no
 * separate overdue/no-record bucketing needed.
 */
function wofCofDaysUntilDue(vehicle: Vehicle): number {
  if (!vehicle.externalId) return Infinity;

  const status = inspectionHistoryStore.getComplianceStatusForVehicle(
    vehicle.externalId,
  )[0];

  return status?.days_until_due ?? Infinity;
}

/**
 * Days until this vehicle's registration is due, or +Infinity if it has no
 * recorded expiry. Registration has no per-row due date like WOF/COF does -
 * it comes from the vehicle's own registrationExpiry field instead.
 */
function registrationDaysUntilDue(vehicle: Vehicle): number {
  return vehicle.registrationExpiry
    ? daysUntil(vehicle.registrationExpiry)
    : Infinity;
}

/**
 * Sorts most-concerning-first (most overdue, then soonest due, then
 * furthest out, then no record) for whichever due date is selected. Copies
 * before sorting since Array.prototype.sort mutates in place and these
 * lists are derived from store state.
 */
function sortByConcern(vehicles: Vehicle[]): Vehicle[] {
  if (sortOption.value === "none") return vehicles;

  const daysUntilDue =
    sortOption.value === "wofCof"
      ? wofCofDaysUntilDue
      : registrationDaysUntilDue;

  return [...vehicles].sort(
    (a, b) => daysUntilDue(a) - daysUntilDue(b),
  );
}

function vehicleMatchesSearch(vehicle: Vehicle): boolean {
  if (!searchQuery.value?.trim()) return true;
  const query = searchQuery.value.toLowerCase();
  return !!(
    vehicle.name?.toLowerCase().includes(query) ||
    vehicle.VIN?.toLowerCase().includes(query) ||
    vehicle.licensePlate?.toLowerCase().includes(query)
  );
}

function vehicleMatchesFilters(vehicle: Vehicle): boolean {
  if (
    notifiedVehicleExternalIds.value?.length &&
    (!vehicle.externalId ||
      !notifiedVehicleExternalIds.value.includes(vehicle.externalId))
  ) {
    return false;
  }

  if (
    selectedFuelTypes.value.length &&
    (!vehicle.fuelType || !selectedFuelTypes.value.includes(vehicle.fuelType))
  ) {
    return false;
  }

  if (selectedIssueFilters.value.length) {
    const complianceStatusRows = vehicle.externalId
      ? inspectionHistoryStore.getComplianceStatusForVehicle(vehicle.externalId)
      : [];
    const inspectionRecords = vehicle.externalId
      ? inspectionHistoryStore.getRecordsForVehicle(vehicle.externalId)
      : [];

    const matchesAnySelectedIssue = selectedIssueFilters.value.some((filter) =>
      vehicleMatchesIssueFilter(
        filter,
        vehicle,
        complianceStatusRows,
        inspectionRecords,
      ),
    );
    if (!matchesAnySelectedIssue) return false;
  }

  if (selectedRucFilters.value.length) {
    const rucStatus = vehicle.externalId
      ? rucStore.getStatusForVehicle(vehicle.externalId)
      : undefined;

    const matchesAnySelectedRucFilter = selectedRucFilters.value.some(
      (filter) => vehicleMatchesRucFilter(filter, rucStatus, vehicle.fuelType),
    );
    if (!matchesAnySelectedRucFilter) return false;
  }

  return true;
}

const hasActiveSearchOrFilters = computed(
  () =>
    !!searchQuery.value?.trim() ||
    !!selectedFuelTypes.value.length ||
    !!selectedIssueFilters.value.length ||
    !!selectedRucFilters.value.length ||
    !!notifiedVehicleExternalIds.value?.length,
);

const filteredCustomVehicles = computed<Vehicle[]>(() =>
  sortByConcern(
    vehicleStore.getCustomVehicles.filter(
      (vehicle) =>
        vehicleMatchesSearch(vehicle) && vehicleMatchesFilters(vehicle),
    ),
  ),
);

const filteredConnectedVehicles = computed<Vehicle[]>(() =>
  sortByConcern(
    vehicleStore.getConnectedVehicles.filter(
      (vehicle) =>
        vehicleMatchesSearch(vehicle) && vehicleMatchesFilters(vehicle),
    ),
  ),
);

function navigateToVehicleDetails(vehicle: Vehicle) {
  dashboardNavStore.showVehicleDetails(vehicle.id);
}
</script>
